import type { ScenarioSpec } from "./types";
import { officeUnderAttackScenario as OUA } from "./office-under-attack.scenario";

/**
 * Social Engineering briefing. The film plays on the reception terminal.
 * Each exercise is one scene borrowed from an existing course. A `stop`
 * parameter ends that scene and offers the way back here.
 */
const VIDEO = "/cdn/sim/awareness-videos/pause-and-verify/pause-and-verify.mp4";
const POSTER = "/cdn/sim/awareness-videos/pause-and-verify/wide/plate.jpg";

const still = (course: string, beat: string) =>
  `/cdn/sim/${course}/customize-stills/${beat}.jpg`;

function sceneLink(scenario: string, beat: string, stop: string): string {
  return `/?scenario=${scenario}&beat=${beat}&return=pause-and-verify&stop=${stop}`;
}

export const pauseAndVerifyScenario: ScenarioSpec = {
  version: 1,
  id: "pause-and-verify",
  title: "Pause and Verify",
  summary: {
    goal: "Learn to pause and verify before you let someone in, approve a payment on a call, join a network, plug in a device, or hand over a login or code.",
    youDo: "Watch the film, then pick practice scenes: a visitor at a secured door, a CEO video call, hotel Wi-Fi, a USB device, a spear-phishing email, a bank call and a QR code.",
    duration: "Core 6-10 min, each scene adds 2-4 min",
    tags: ["verification", "social_engineering", "physical_security", "deepfakes", "voice_phishing", "qr_phishing", "public_wifi", "usb_devices"],
    audience: ["all_staff"],
    needs: ["sound"],
  },
  topic: "Pause and verify before you let someone in, onto a call, or onto the computer",
  language: "en",
  passThreshold: 60,
  allowFreeExplore: false,
  voiceAttackers: OUA.voiceAttackers,
  scene: OUA.scene,

  devices: [
    {
      id: "se_terminal",
      kind: "breachbrief",
      config: {
        noticeTag: "SOCIAL ENGINEERING",
        noticeKicker: "SECURITY AWARENESS",
        title: "Pause and Verify",
        offLabel: "Switch on the welcome terminal",
        voiceId: "onyx",
        progressKey: "pause-and-verify",
        idleImage: "/cdn/sim/pause-and-verify/terminal-idle.jpg",
        videoFirst: true,
        lines: [
          "A normal day at the office. Someone may try to talk you into a decision.",
          "Watch the film, then practise the scenes. Each one brings you back here.",
        ],
        hubIntro: "Watch the film, then practise a scene. You return here when it ends.",
        hubVideo: {
          title: "Pause and verify",
          blurb: "Why a plausible person, call, or page is not proof.",
          src: VIDEO,
          poster: POSTER,
        },
        exercisesKicker: "7 interactive exercises",
        exercisesLabel: "Practise",
        exercises: [
          {
            id: "se-door",
            label: "A visitor at the door",
            blurb: "Someone at the secured door wants you to let them in.",
            key: "door",
            completionBeatId: "se-door-done",
            href: sceneLink("office-under-attack", "b_cam_badge", "b_cam_desk"),
            icon: "chapters",
            imageUrl: still("office-under-attack", "b_badge_decision"),
            actionLabel: "Practise",
            doneLabel: "Done",
          },
          {
            id: "se-deepfake",
            label: "Deepfake video meeting",
            blurb: "An executive is on the video call and wants a payment.",
            key: "deepfake",
            completionBeatId: "se-deepfake-done",
            href: sceneLink("office-under-attack", "b_ep9_title", "b_cam_ai"),
            icon: "chapters",
            imageUrl: still("office-under-attack", "b_ep9_call"),
            actionLabel: "Practise",
            doneLabel: "Done",
          },
          {
            id: "se-wifi",
            label: "Rogue Wi-Fi",
            blurb: "A network that looks like the hotel wants your login.",
            key: "wifi",
            completionBeatId: "se-wifi-done",
            href: sceneLink("pc-to-darkweb", "b_wifi_title", "b_teach_wifi,b_done_wifi,b_return_hub"),
            icon: "chapters",
            imageUrl: still("pc-to-darkweb", "b_wifi"),
            actionLabel: "Practise",
            doneLabel: "Done",
          },
          {
            id: "se-usb",
            label: "Bad USB",
            blurb: "A device you did not expect is waiting to be plugged in.",
            key: "usb",
            completionBeatId: "se-usb-done",
            href: sceneLink("pc-to-darkweb", "b_usb_title", "b_done_usb,b_return_hub"),
            icon: "chapters",
            imageUrl: still("pc-to-darkweb", "b_usb_decision"),
            actionLabel: "Practise",
            doneLabel: "Done",
          },
          {
            id: "se-spear",
            label: "Spear phishing",
            blurb: "A message built from things you posted, then a follow-up.",
            key: "spear",
            completionBeatId: "se-spear-done",
            href: sceneLink("spear-phishing-osint", "b_rest_establish", "b_quiz,b_debrief"),
            icon: "chapters",
            imageUrl: still("spear-phishing-osint", "b_lure1"),
            actionLabel: "Practise",
            doneLabel: "Done",
          },
          {
            id: "se-voice",
            label: "Voice phishing",
            blurb: "The phone rings. The caller already knows about the email.",
            key: "voice",
            completionBeatId: "se-voice-done",
            href: sceneLink("phishing-office", "b_to_phone", "b_quiz"),
            icon: "chapters",
            imageUrl: still("phishing-office", "b_call"),
            actionLabel: "Practise",
            doneLabel: "Done",
          },
          {
            id: "se-qr",
            label: "QR code phishing",
            blurb: "A free-cocktail sign asks you to scan, then the TV tells the rest.",
            key: "qr",
            completionBeatId: "se-qr-done",
            href:
              "/?spec=" +
              encodeURIComponent("/cdn/sim/bar-walkable/bar-walkable-spec.json") +
              "&beat=b_qr_zoom&return=pause-and-verify&stop=b_qr_teach_bad,b_qr_teach_safe",
            icon: "chapters",
            imageUrl: still("bar-walkable", "b_qr_zoom"),
            actionLabel: "Practise",
            doneLabel: "Done",
          },
        ],
        cards: [],
        finishEarlyHint: "You can take the quiz now, or watch the film and practise a scene first.",
      },
    },
  ],

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Pause and Verify",
      kicker: "Reception",
      guide: { title: "Start", body: "You start at an unattended reception desk in the office. The welcome terminal plays a short film on why a convincing person, call or page is not proof, and lets you practise scenes from other courses before a short quiz." },
    },
    { id: "b_cam_terminal", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_hub",
      type: "device",
      deviceId: "se_terminal",
      guide: {
        title: "Reception terminal",
        body: "Watch the film, then choose a scene. You come back here when it ends.",
      },
      completeOn: { action: "continue" },
    },
    {
      id: "b_quiz",
      type: "quiz",
      title: "Check what you would do",
      quiz: [
        {
          id: "q_door",
          title: "Question 1",
          prompt: "A visitor at the secured door says they are late and asks you to let them in. What do you do?",
          options: [
            { id: "a", label: "Open the door. They look like they belong here.", icon: "x" },
            { id: "b", label: "Leave the door closed and send them to reception to be checked in.", correct: true, icon: "check" },
            { id: "c", label: "Take the delivery yourself and walk them to a desk.", icon: "alert" },
          ],
          rationale: "A secured door is checked at reception. A friendly story is not a badge.",
        },
        {
          id: "q_call",
          title: "Question 2",
          prompt: "Someone who looks and sounds like an executive is on a video call and wants a payment today. What is enough to approve it?",
          options: [
            { id: "a", label: "Seeing and hearing them on the call.", icon: "x" },
            { id: "b", label: "Nothing on that call. Verify through a number or process you already trust.", correct: true, icon: "check" },
            { id: "c", label: "The urgent chat message that arrived just before the call.", icon: "alert" },
          ],
          rationale: "A face and a voice on a call can be copied. Urgency from several channels is pressure, not proof.",
        },
        {
          id: "q_wifi",
          title: "Question 3",
          prompt: "A Wi-Fi name looks like the hotel, and it does not ask for the hotel password. What do you do?",
          options: [
            { id: "a", label: "Join it. The name is close enough.", icon: "x" },
            { id: "b", label: "Use the network you were given, or your own mobile data.", correct: true, icon: "check" },
            { id: "c", label: "Join it only to check email, then forget the network.", icon: "alert" },
          ],
          rationale: "A lookalike network can sit between you and the real one. A familiar name is not the hotel's network.",
        },
        {
          id: "q_usb",
          title: "Question 4",
          prompt: "You find a USB device you were not expecting. What do you do with it?",
          options: [
            { id: "a", label: "Plug it in to see who it belongs to.", icon: "x" },
            { id: "b", label: "Do not plug it in. Hand it to IT or reception.", correct: true, icon: "check" },
            { id: "c", label: "Plug it into a personal laptop, because that is not the work network.", icon: "alert" },
          ],
          rationale: "A device you did not expect can run something the moment it is connected. Ownership is not worth finding out that way.",
        },
        {
          id: "q_qr",
          title: "Question 5",
          prompt: "A sign offers a free drink if you scan a code, and the page asks for your work login. What do you do?",
          options: [
            { id: "a", label: "Sign in. A drink voucher is harmless.", icon: "x" },
            { id: "b", label: "Do not use a work login. Close the page.", correct: true, icon: "check" },
            { id: "c", label: "Sign in, then change your password afterwards.", icon: "alert" },
          ],
          rationale: "The offer is the lure. A work login on a page you reached from a sticker is the theft.",
        },
        {
          id: "q_voice",
          title: "Question 6",
          prompt: "A caller knows about an email you just received and asks you to read out a code. What do you do?",
          options: [
            { id: "a", label: "Read the code. They already know about the email.", icon: "x" },
            { id: "b", label: "Hang up and check through a number or process you already trust.", correct: true, icon: "check" },
            { id: "c", label: "Read the code if they say they are from the bank.", icon: "alert" },
          ],
          rationale: "Knowing about the email is part of the setup. A code read aloud finishes it. You call back on a number you already had.",
        },
      ],
    },
  ],

  edges: [
    { from: "b_intro", to: "b_cam_terminal" },
    { from: "b_cam_terminal", to: "b_hub" },
    { from: "b_hub", to: "b_quiz", on: "finish" },
  ],

  initialBeatId: "b_intro",
};
